import React, { useEffect, useMemo, useState } from "react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package2, PackageOpen, PackageCheck, Boxes, Send, Loader2,
  RefreshCw, Search, ChevronDown, ChevronRight, X, Plus, Trash2, Check,
} from "lucide-react";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const FIELD = "w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm";

/** Store Module (rewired iter 84).
 * Store users:
 *   - Raise stock purchase requests (Admin/Accounts create the actual POs).
 *   - Receive POs line by line — status flips to "received" only when
 *     every line's qty_received matches the ordered qty.
 *   - Issue inventory to projects with an inline qty input + search box.
 */
export default function Store() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [projects, setProjects] = useState([]);
  const [dash, setDash] = useState(null);
  const [receiveStatus, setReceiveStatus] = useState("pending"); // "pending" | "received" | "all"
  const [inventoryFilter, setInventoryFilter] = useState("all"); // "all" | "on_hand" | "issued"
  const bump = () => setRefreshKey((k) => k + 1);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const jumpToReceive = (status) => {
    setReceiveStatus(status);
    setTimeout(() => scrollTo("store-receive-anchor"), 40);
  };
  const jumpToInventory = (filter) => {
    setInventoryFilter(filter);
    setTimeout(() => scrollTo("store-inventory-anchor"), 40);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: pr } = await api.get("/project/");
        setProjects((pr || []).filter((p) => !p.archived));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/costing/store-dashboard");
        setDash(data);
      } catch (e) { /* silent */ }
    })();
  }, [refreshKey]);

  return (
    <DesktopLayout>
      <div className="max-w-[1600px] mx-auto p-6 space-y-4" data-testid="store-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">Store · Inventory</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">Store</h1>
            <div className="text-xs text-slate-500 mt-1">
              Raise purchase requests, receive vendor deliveries line-by-line,
              and issue materials to projects.
            </div>
          </div>
          <button type="button" onClick={bump}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="store-refresh">
            <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
          </button>
        </div>

        <StoreDashboardTiles dash={dash}
          onPosRaised={() => jumpToReceive("all")}
          onPending={() => jumpToReceive("pending")}
          onReceived={() => jumpToReceive("received")}
          onIssued={() => jumpToInventory("issued")}
          onOnHand={() => jumpToInventory("on_hand")} />

        <RequestStockCard onCreated={bump} />

        <div id="store-receive-anchor" className="space-y-4">
          <ReceiveItemsCard refreshKey={refreshKey} onReceived={bump}
            status={receiveStatus} onStatusChange={setReceiveStatus} />
          <IssueToProjectCard refreshKey={refreshKey} projects={projects} onIssued={bump} />
        </div>

        <div id="store-inventory-anchor">
          <InventoryCard refreshKey={refreshKey}
            filter={inventoryFilter} onFilterChange={setInventoryFilter} />
        </div>
      </div>
    </DesktopLayout>
  );
}

function StoreDashboardTiles({ dash, onPosRaised, onPending, onReceived, onIssued, onOnHand }) {
  const d = dash || {};
  const tiles = [
    { label: "POs Raised",   value: d.pos_raised,      sub: `${d.stock_pos ?? 0} stock POs`, tone: "#0F172A", icon: Package2,     onClick: onPosRaised },
    { label: "Pending",      value: d.pos_pending,     sub: fmt(d.pos_pending_value),        tone: "#C2410C", icon: PackageOpen,  onClick: onPending },
    { label: "Received",     value: d.pos_received,    sub: fmt(d.pos_received_value),       tone: "#15803D", icon: PackageCheck, onClick: onReceived },
    { label: "Issued",       value: d.issued_count,    sub: fmt(d.issued_value),             tone: "#065F46", icon: Send,         onClick: onIssued },
    { label: "On Hand",      value: d.inventory_items, sub: fmt(d.inventory_value),          tone: "#7C2D12", icon: Boxes,        onClick: onOnHand },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="store-dashboard-tiles">
      {tiles.map((t) => (
        <button key={t.label} type="button" onClick={t.onClick}
          className="bg-white border border-slate-200 rounded-sm p-4 text-left transition
                     hover:border-slate-400 hover:shadow-md focus:outline-none
                     focus:ring-2 focus:ring-slate-300 cursor-pointer group"
          data-testid={`store-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-2">
            <t.icon className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" style={{ color: t.tone }} />
            <div className="hk-tiny-label">{t.label}</div>
          </div>
          <div className="text-2xl font-black tracking-tight mt-1" style={{ color: t.tone }}>
            {dash === null ? "—" : (t.value ?? 0)}
          </div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">{dash === null ? "" : t.sub}</div>
        </button>
      ))}
    </div>
  );
}

function CardShell({ title, subtitle, icon: Icon, children, testId, accent = "#B45309", right = null }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={testId}>
      <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2.25} />}
          <div>
            <h2 className="text-xl font-black tracking-tight leading-tight"
              style={{ color: accent, fontFamily: "Chivo" }}>{title}</h2>
            {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Request Stock — raises a purchase_request. Admin/Accounts creates
 * the actual PO from the Purchase Board.
 * ------------------------------------------------------------------ */
function RequestStockCard({ onCreated }) {
  const emptyLine = () => ({ item_name: "", quantity: "", unit: "", notes: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  const load = async () => {
    setLoadingPending(true);
    try {
      const { data } = await api.get("/costing/stock-requests?status=open");
      setPending(data.requests || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingPending(false); }
  };
  useEffect(() => { load(); }, []);

  const setLine = (i, f, v) => setLines((L) => L.map((ln, idx) => idx === i ? { ...ln, [f]: v } : ln));
  const addLine = () => setLines((L) => [...L, emptyLine()]);
  const rmLine = (i) => setLines((L) => L.length > 1 ? L.filter((_, idx) => idx !== i) : [emptyLine()]);

  const submit = async () => {
    const items = lines
      .filter((l) => l.item_name.trim() && Number(l.quantity) > 0)
      .map((l) => ({
        item_name: l.item_name.trim(),
        quantity: Number(l.quantity),
        unit: l.unit.trim() || null,
        notes: l.notes.trim() || null,
      }));
    if (items.length === 0) { toast.error("Add at least one valid line"); return; }
    setSaving(true);
    try {
      await api.post("/costing/stock-requests", { items, notes: notes.trim() || null });
      toast.success(`${items.length} stock request${items.length === 1 ? "" : "s"} raised`);
      setLines([emptyLine()]);
      setNotes("");
      await load();
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cancel = async (req) => {
    if (!window.confirm(`Cancel request for "${req.item_name}"?`)) return;
    try {
      await api.delete(`/costing/stock-requests/${req.id}`);
      toast.success("Cancelled");
      await load();
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <CardShell title="Request Stock" icon={Package2}
      subtitle="Raise a purchase request. Admin/Accounts will create the PO."
      testId="store-request-stock-card">
      <div className="p-3 space-y-2">
        <div className="space-y-1" data-testid="stock-request-lines">
          {lines.map((ln, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 items-start">
              <input className={`${FIELD} col-span-5`} placeholder="Item name"
                value={ln.item_name}
                onChange={(e) => setLine(i, "item_name", e.target.value)}
                data-testid={`stock-request-line-name-${i}`} />
              <input className={`${FIELD} col-span-2`} type="number" min="0" step="0.01"
                placeholder="Qty" value={ln.quantity}
                onChange={(e) => setLine(i, "quantity", e.target.value)}
                data-testid={`stock-request-line-qty-${i}`} />
              <input className={`${FIELD} col-span-1`} placeholder="Unit"
                value={ln.unit}
                onChange={(e) => setLine(i, "unit", e.target.value)} />
              <input className={`${FIELD} col-span-3`} placeholder="Line notes (optional)"
                value={ln.notes}
                onChange={(e) => setLine(i, "notes", e.target.value)} />
              <button type="button" onClick={() => rmLine(i)}
                className="col-span-1 p-1.5 text-red-600 hover:bg-red-50 rounded-sm"
                data-testid={`stock-request-line-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addLine}
          className="text-[11px] font-bold uppercase text-amber-700 hover:text-amber-900"
          data-testid="stock-request-add-line">
          <Plus className="w-3 h-3 inline" /> Add line
        </button>
        <textarea className={FIELD} rows={2} placeholder="Overall notes (optional)"
          value={notes} onChange={(e) => setNotes(e.target.value)} />
        <button type="button" onClick={submit} disabled={saving}
          className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs uppercase"
          data-testid="stock-request-submit">
          {saving && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />} Raise Purchase Request
        </button>
      </div>
      <div className="border-t border-slate-100 p-3">
        <div className="hk-tiny-label mb-1 text-slate-500">Pending Requests</div>
        {loadingPending ? (
          <div className="text-[11px] text-slate-400">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="text-[11px] text-slate-400" data-testid="stock-request-pending-empty">
            No open requests. Admin/Accounts convert requests to POs.
          </div>
        ) : (
          <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="stock-request-pending-list">
            {pending.map((r) => (
              <PendingRequestRow key={r.id} req={r} onCancel={() => cancel(r)} />
            ))}
          </ul>
        )}
      </div>
    </CardShell>
  );
}

/* ------------------------------------------------------------------
 * Pending Request row — click OPEN to reveal full details in-place.
 * ------------------------------------------------------------------ */
function PendingRequestRow({ req, onCancel }) {
  const [open, setOpen] = useState(false);
  const requestedAt = (req.requested_at || "").slice(0, 10);
  return (
    <li className="border-b border-slate-50 last:border-0" data-testid={`stock-request-item-${req.id}`}>
      <div className="flex items-center justify-between gap-2 py-1 text-[11px]">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-800 truncate">{req.item_name}</div>
          <div className="text-[10px] text-slate-500">
            {req.quantity}{req.unit ? ` ${req.unit}` : ""}
            {req.notes ? ` · ${req.notes}` : ""}
          </div>
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 text-[9px] font-bold uppercase hover:bg-amber-200 transition"
          data-testid={`stock-request-badge-${req.id}`}
          aria-expanded={open}>
          {open ? "hide" : "open"}
        </button>
        <button type="button" onClick={onCancel}
          className="text-slate-400 hover:text-red-600"
          data-testid={`stock-request-cancel-${req.id}`}
          title="Cancel this request">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (
        <div className="pl-2 pb-2 text-[10px] text-slate-600 grid grid-cols-2 gap-x-3 gap-y-0.5"
          data-testid={`stock-request-detail-${req.id}`}>
          <div><span className="text-slate-400">Requested by:</span> <b>{req.requested_by_name}</b></div>
          <div><span className="text-slate-400">On:</span> <span className="font-mono">{requestedAt}</span></div>
          <div className="col-span-2"><span className="text-slate-400">Status:</span> <b className="uppercase">{req.status}</b></div>
          {req.notes && <div className="col-span-2 italic text-slate-500">"{req.notes}"</div>}
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------
 * Receive Items — per-line partial receive.
 * ------------------------------------------------------------------ */
function ReceiveItemsCard({ refreshKey, onReceived, status, onStatusChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/costing/purchase-board?status=${status || "pending"}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey, status]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((po) => (
      (po.po_number || "").toLowerCase().includes(needle)
      || (po.vendor_name || "").toLowerCase().includes(needle)
      || (po.project_pid || "").toLowerCase().includes(needle)
      || (po.project_name || "").toLowerCase().includes(needle)
      || (po.lines || []).some((ln) => (ln.item_name || "").toLowerCase().includes(needle))
    ));
  }, [rows, q]);

  const statusOptions = [
    { key: "pending",  label: "Pending" },
    { key: "received", label: "Received" },
    { key: "all",      label: "All" },
  ];

  const subtitleMap = {
    pending:  "Enter received quantities per line. PO closes only when every line is fully received.",
    received: "Fully-received POs — click any row to see the received qty per line.",
    all:      "Every PO on the board, both pending and received.",
  };

  return (
    <CardShell title="Receive Items" icon={PackageOpen}
      subtitle={subtitleMap[status] || subtitleMap.pending}
      testId="store-receive-card" accent="#0369A1"
      right={
        <div className="inline-flex bg-slate-100 rounded-sm p-0.5" data-testid="store-receive-status-tabs">
          {statusOptions.map((o) => (
            <button key={o.key} type="button" onClick={() => onStatusChange(o.key)}
              className={`px-2 py-1 rounded-sm text-[10px] font-bold uppercase transition ${
                (status || "pending") === o.key
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`store-receive-status-${o.key}`}>
              {o.label}
            </button>
          ))}
        </div>
      }>
      <div className="p-3 border-b border-slate-100">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full border border-slate-300 rounded-sm pl-7 pr-3 py-1.5 text-xs"
            placeholder="Search by PO number, vendor, project, or item"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="store-receive-search" />
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-slate-400 text-xs">Loading POs…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-xs" data-testid="store-receive-empty">
          {q ? "No POs match your search." :
            status === "received"
              ? "Nothing received yet."
              : status === "all"
                ? "No POs at all — start with a stock request or a project PO."
                : "Nothing pending — every PO is fully received."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
          {filtered.map((po) => (
            <POReceiveRow key={po.id} po={po} open={openId === po.id}
              onToggle={() => setOpenId(openId === po.id ? null : po.id)}
              onReceived={() => { load(); onReceived && onReceived(); }} />
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function POReceiveRow({ po, open, onToggle, onReceived }) {
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({}); // { line_index: qty_to_receive_now }

  const lines = po.lines || [];
  const linesFull = useMemo(
    () => lines.map((ln) => ({
      qty: Number(ln.quantity || 0),
      received: Number(ln.qty_received || 0),
    })),
    [lines]
  );
  const anyOpen = linesFull.some((l) => l.received < l.qty);

  const receive = async () => {
    // Compact non-zero, positive deltas.
    const line_receipts = {};
    for (const [k, v] of Object.entries(values)) {
      const n = Number(v);
      if (n > 0) line_receipts[k] = n;
    }
    if (Object.keys(line_receipts).length === 0) {
      toast.error("Enter at least one received qty");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/costing/purchase-board/${po.id}/receive`, { line_receipts });
      const stockChip = po.is_stock_po ? " Items are now in Inventory & Issue to Project." : "";
      toast.success(data.status === "received"
        ? `${po.po_number} fully received.${stockChip}`
        : `${po.po_number} — partial receipt saved.${stockChip}`);
      setValues({});
      onReceived && onReceived();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <li data-testid={`store-receive-row-${po.id}`}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left">
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
          <div className="min-w-0">
            <div className="font-mono font-bold text-sm text-slate-900 truncate flex items-center gap-1.5">
              {po.po_number}
              {po.is_stock_po ? (
                <span className="px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 text-[9px] font-bold uppercase">stock</span>
              ) : null}
            </div>
            <div className="font-mono font-bold text-xs text-slate-700 truncate">
              {po.is_stock_po
                ? (po.vendor_name || "—")
                : (
                    <>
                      {po.project_pid || po.project_name || "PROJECT"}
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="text-slate-600">{po.vendor_name}</span>
                    </>
                  )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-[10px] font-mono text-slate-500">
            {linesFull.filter((l) => l.received >= l.qty).length}/{linesFull.length} lines
          </div>
          <div className="text-[11px] text-slate-600 font-mono">{fmt(po.total)}</div>
        </div>
      </button>
      {open && (
        <div className="bg-slate-50 border-t border-slate-100 p-3 space-y-2" data-testid={`store-receive-lines-${po.id}`}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-slate-500 uppercase text-[9px] tracking-wider">
                <th className="pb-1">Item</th>
                <th className="pb-1 text-right">Ordered</th>
                <th className="pb-1 text-right">Prev Rcvd</th>
                <th className="pb-1 text-right">Receiving Now</th>
                <th className="pb-1 text-right">After</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => {
                const qty = Number(ln.quantity || 0);
                const prev = Number(ln.qty_received || 0);
                const now = Number(values[i] || 0);
                const remaining = Math.max(0, qty - prev);
                const after = prev + now;
                const complete = prev >= qty;
                return (
                  <tr key={i} className="border-t border-slate-200">
                    <td className="py-1.5">
                      <div className="font-semibold text-slate-800 truncate">{ln.item_name}</div>
                      {ln.unit && <div className="text-[9px] text-slate-500">{ln.unit}</div>}
                    </td>
                    <td className="py-1.5 text-right font-mono">{qty}</td>
                    <td className="py-1.5 text-right font-mono text-slate-500">{prev}</td>
                    <td className="py-1.5 text-right">
                      {complete ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase">
                          <Check className="w-3 h-3" /> Done
                        </span>
                      ) : (
                        <input type="number" min="0" step="0.01" max={remaining}
                          className="w-24 border border-slate-300 rounded-sm px-2 py-1 text-right font-mono"
                          value={values[i] ?? ""}
                          placeholder={String(remaining)}
                          onChange={(e) => setValues({ ...values, [i]: e.target.value })}
                          data-testid={`store-receive-input-${po.id}-${i}`} />
                      )}
                    </td>
                    <td className={`py-1.5 text-right font-mono ${after >= qty ? "text-emerald-700 font-bold" : "text-slate-500"}`}>
                      {after}/{qty}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {anyOpen && (
            <button type="button" onClick={receive} disabled={saving}
              className="px-3 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold uppercase inline-flex items-center gap-1"
              data-testid={`store-receive-submit-${po.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageCheck className="w-3 h-3" />}
              Save Received Quantities
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------
 * Issue to Project — inline qty input + search box.
 * ------------------------------------------------------------------ */
function IssueToProjectCard({ refreshKey, projects, onIssued }) {
  const [inv, setInv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState("");
  const [issuing, setIssuing] = useState(null);
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/costing/inventory");
      setInv(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return inv;
    return inv.filter((i) => (i.name || "").toLowerCase().includes(needle));
  }, [inv, q]);

  const issue = async (item) => {
    if (!projectId) { toast.error("Pick a project first"); return; }
    const qty = Number(qtyDrafts[item.id] || 0);
    if (!qty || qty <= 0) { toast.error("Enter a positive quantity"); return; }
    if (qty > item.on_hand_qty) { toast.error(`Only ${item.on_hand_qty} in stock`); return; }
    setIssuing(item.id);
    try {
      await api.post(`/costing/inventory/${item.id}/issue`, { project_id: projectId, qty });
      const p = projects.find((x) => x.id === projectId);
      toast.success(`Issued ${qty} ${item.unit || ""} of ${item.name} → ${p?.project_id || projectId}`);
      setQtyDrafts((d) => ({ ...d, [item.id]: "" }));
      await load();
      onIssued && onIssued();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setIssuing(null); }
  };

  return (
    <CardShell title="Issue to Project" icon={Send}
      subtitle="Pick a project, search an item, type qty, tap Issue."
      testId="store-issue-card" accent="#065F46">
      <div className="p-3 border-b border-slate-100 space-y-2">
        <select className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
          value={projectId} onChange={(e) => setProjectId(e.target.value)}
          data-testid="store-issue-project-select">
          <option value="">Select project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.project_id} — {p.name || "Untitled"}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full border border-slate-300 rounded-sm pl-7 pr-3 py-1.5 text-xs"
            placeholder="Search item name" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="store-issue-search" />
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-slate-400 text-xs">Loading inventory…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-xs" data-testid="store-issue-empty">
          {q ? "No items match your search." : "Nothing in stock yet."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
          {filtered.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-[12px]"
              data-testid={`store-issue-item-${item.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{item.name}</div>
                <div className="text-[11px] text-slate-500">
                  On hand: <b>{item.on_hand_qty}</b>{item.unit ? ` ${item.unit}` : ""}
                  {" · "}avg {fmt(item.avg_unit_price)}
                </div>
              </div>
              <input type="number" min="0" step="0.01" max={item.on_hand_qty}
                className="w-24 border border-slate-300 rounded-sm px-2 py-1.5 text-right font-mono text-xs"
                placeholder="Qty" value={qtyDrafts[item.id] ?? ""}
                onChange={(e) => setQtyDrafts({ ...qtyDrafts, [item.id]: e.target.value })}
                data-testid={`store-issue-qty-${item.id}`} />
              <button type="button" onClick={() => issue(item)}
                disabled={!projectId || issuing === item.id || !qtyDrafts[item.id]}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold uppercase disabled:opacity-40"
                data-testid={`store-issue-btn-${item.id}`}>
                {issuing === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Issue
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

/* ------------------------------------------------------------------
 * Inventory Snapshot (searchable ledger).
 * ------------------------------------------------------------------ */
function InventoryCard({ refreshKey, filter, onFilterChange }) {
  const [inv, setInv] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const [movements, setMovements] = useState([]);

  const current = filter || "all";
  const isMovementsView = current === "issued";

  const load = async () => {
    setLoading(true);
    try {
      if (isMovementsView) {
        const { data } = await api.get("/costing/inventory/movements?movement_type=stock_issue&limit=300");
        setMoves(data.movements || []);
      } else {
        const url = current === "on_hand"
          ? "/costing/inventory"
          : "/costing/inventory?include_empty=1";
        const { data } = await api.get(url);
        setInv(data.items || []);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey, current]);

  const filteredItems = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return inv;
    return inv.filter((i) => (i.name || "").toLowerCase().includes(needle));
  }, [inv, q]);

  const filteredMoves = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return moves;
    return moves.filter((m) =>
      (m.item_name || "").toLowerCase().includes(needle) ||
      (m.project_pid || "").toLowerCase().includes(needle) ||
      (m.project_name || "").toLowerCase().includes(needle));
  }, [moves, q]);

  const toggleItem = async (item) => {
    if (openItem === item.id) { setOpenItem(null); setMovements([]); return; }
    setOpenItem(item.id);
    try {
      const { data } = await api.get(`/costing/inventory/${item.id}/movements`);
      setMovements(data.movements || []);
    } catch (e) { toast.error(formatApiError(e)); setMovements([]); }
  };

  const filterTabs = [
    { key: "all",     label: "All Items" },
    { key: "on_hand", label: "On Hand" },
    { key: "issued",  label: "Issued (log)" },
  ];

  return (
    <CardShell title="Inventory" icon={Boxes}
      subtitle={isMovementsView
        ? "Every issue made from stock to a project. Newest first."
        : "Everything ever received into stock. Click a row for full history."}
      testId="store-inventory-card" accent="#7C2D12"
      right={
        <div className="inline-flex bg-slate-100 rounded-sm p-0.5" data-testid="store-inventory-tabs">
          {filterTabs.map((o) => (
            <button key={o.key} type="button" onClick={() => onFilterChange(o.key)}
              className={`px-2 py-1 rounded-sm text-[10px] font-bold uppercase transition ${
                current === o.key
                  ? "bg-white shadow-sm text-slate-900"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`store-inventory-tab-${o.key}`}>
              {o.label}
            </button>
          ))}
        </div>
      }>
      <div className="p-3 border-b border-slate-100">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <input className="w-full border border-slate-300 rounded-sm pl-7 pr-3 py-1.5 text-xs"
            placeholder={isMovementsView ? "Search item or project" : "Search item name"}
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="store-inventory-search" />
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-slate-400 text-xs">Loading…</div>
      ) : isMovementsView ? (
        filteredMoves.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-xs" data-testid="store-inventory-empty">
            No stock issues yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto"
            data-testid="store-issue-log-list">
            {filteredMoves.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-[11px]">
                <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-amber-100 text-amber-800">OUT</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{m.item_name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    → {m.project_pid || m.project_name || m.project_id} · by {m.by_name}
                  </div>
                </div>
                <div className="text-right shrink-0 font-mono">
                  <div className="font-bold">{m.qty}{m.unit ? ` ${m.unit}` : ""}</div>
                  <div className="text-[9px] text-slate-500">{fmt((m.qty || 0) * (m.unit_price || 0))}</div>
                </div>
                <span className="text-[9px] font-mono text-slate-400 shrink-0">{(m.at || "").slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )
      ) : filteredItems.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-xs">No inventory items match.</div>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {filteredItems.map((item) => (
            <li key={item.id} data-testid={`store-inv-row-${item.id}`}>
              <button type="button" onClick={() => toggleItem(item)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {openItem === item.id ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-800 truncate">{item.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {item.unit || "—"} · avg {fmt(item.avg_unit_price)}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold text-sm ${item.on_hand_qty > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                    {item.on_hand_qty}
                  </div>
                  <div className="text-[10px] text-slate-500">on hand</div>
                </div>
              </button>
              {openItem === item.id && (
                <div className="bg-slate-50 border-t border-slate-100 px-3 py-2 max-h-56 overflow-y-auto">
                  {movements.length === 0 ? (
                    <div className="text-[11px] text-slate-400 text-center py-2">No movements yet.</div>
                  ) : (
                    <ul className="space-y-1">
                      {movements.map((m) => (
                        <li key={m.id} className="text-[11px] flex items-center justify-between gap-2">
                          <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase shrink-0 ${
                            m.movement_type === "stock_receipt" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          }`}>
                            {m.movement_type === "stock_receipt" ? "IN" : "OUT"}
                          </span>
                          <span className="font-mono">{m.qty}{item.unit ? ` ${item.unit}` : ""}</span>
                          <span className="flex-1 truncate text-slate-500">
                            {m.movement_type === "stock_receipt" ? `via ${m.po_number || "PO"}` : `→ ${m.project_pid || m.project_name || m.project_id}`}
                          </span>
                          <span className="text-slate-400 shrink-0">{(m.at || "").slice(0, 10)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}
